import { useState, useEffect } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useBrainImages } from "@/api/queries/brainImages";
import { useSetBrainSpec } from "@/api/mutations/agents";
import type { Agent } from "@/types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agent: Agent;
}

const UNSET = "__unset__";

export function SetBrainSpecDialog({ open, onOpenChange, agent }: Props) {
  const imagesQuery = useBrainImages();
  const mutation = useSetBrainSpec();
  const [selected, setSelected] = useState<string>(agent.brainSpec?.imageId ?? UNSET);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSelected(agent.brainSpec?.imageId ?? UNSET);
      setServerError(null);
    }
  }, [open, agent.brainSpec?.imageId]);

  const activeImages = (imagesQuery.data ?? []).filter((i) => i.active);

  function handleSubmit() {
    const imageId = selected === UNSET ? null : selected;
    mutation.mutate(
      { id: agent.id, imageId },
      {
        onSuccess: () => {
          toast.success(imageId ? `Brain-Image → ${imageId}` : "Brain-Image entfernt (default)");
          onOpenChange(false);
        },
        onError: (err) => {
          const msg = (err as Error).message ?? "Unbekannter Fehler";
          setServerError(msg);
          toast.error(`BrainSpec fehlgeschlagen: ${msg}`);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="brainspec-dialog">
        <DialogHeader>
          <DialogTitle>Brain-Image ändern</DialogTitle>
          <DialogDescription>
            Welches Brain-Image für diesen Agent verwendet wird. Nur aktive Images werden angezeigt.
          </DialogDescription>
        </DialogHeader>

        {agent.status !== "offline" && (
          <div
            className="flex items-start gap-2 rounded-md bg-yellow/10 border border-yellow/30 p-3 text-sm"
            data-testid="brainspec-restart-hint"
          >
            <AlertTriangle className="h-4 w-4 text-yellow mt-0.5 shrink-0" />
            <div className="text-yellow">
              Brain läuft aktuell ({agent.status}). Image-Wechsel wird erst beim nächsten Container-Relaunch
              wirksam — für sofortigen Effekt danach "Set Assignment" auf gleichen Node klicken
              (triggert Stop + Launch mit neuem Image).
            </div>
          </div>
        )}

        <div className="space-y-1">
          <Label>Brain-Image</Label>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger data-testid="brainspec-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={UNSET}>— Default-Image</SelectItem>
              {activeImages.map((i) => (
                <SelectItem key={i.id} value={i.id}>
                  {i.name} ({i.imageRef})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {serverError && (
          <div className="text-sm text-red" data-testid="brainspec-error">
            {serverError}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="brainspec-cancel">
            Abbrechen
          </Button>
          <Button onClick={handleSubmit} disabled={mutation.isPending} data-testid="brainspec-submit">
            {mutation.isPending ? "..." : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
